import React, { useEffect, useState, useContext, useCallback } from 'react';
import isIBAN from 'validator/lib/isIBAN';
import {
  ComparatorContext,
  useField,
  useFormService,
} from '@kizi/comparator-core';
import { BasicInput } from '../wrappers/inputs/withFormField';
import { Div, P } from '@kizi/core-components';

const InvalidIbanError =
  'Oeps! Er gaat iets mis bij het vinden van je IBAN. Heb jij een buitenlands (SEPA) IBAN nummer wat je probeert in te voeren? Neem dan contact op met onze klantenservice. Zij helpen je graag verder.';

export const normalizeIBAN = (val) => {
  if (!val) {
    return val;
  }

  const value = val.replace(/[^0-9a-zA-Z]+/g, '');

  if (
    value.match(
      /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/i
    )
  ) {
    const nums = value
      .substr(8)
      .replace(/(.{4})/g, '$1 ')
      .trim();
    return `${value.substr(0, 4)} ${value.substr(4, 4)} ${nums}`.toUpperCase();
  }
  return value.toUpperCase();
};

const checkIban = async (iban) => {
  const response = await fetch(
    `${process.env.KIZI_PUBLIC_API_BASE_URL}/iban/check/${iban}`,
    {
      method: 'GET',
      headers: {
        'Content-type': 'application/json',
        Authorization: `Basic ${process.env.KIZI_PUBLIC_API_AUTH_TOKEN}`,
      },
    }
  );

  const { data } = await response.json();

  if (data === 'Dit is geen geldig IBAN nummer.') {
    throw new Error(data);
  }
};

const getFootnoteLabel = (context, selectedProduct, stepOne) => {
  if (context.name === 'car' && selectedProduct.provider.id === '67') {
    const isMainDriver =
      stepOne.find((obj) => obj.name === '"ismaindriver"')?.value === 'ja';
    const initials = isMainDriver
      ? stepOne.find((obj) => obj.name === 'initials')?.value
      : stepOne.find((obj) => obj.name === 'initials_applicant')?.value;
    const lastname = isMainDriver
      ? stepOne.find((obj) => obj.name === 'lastname')?.value
      : stepOne.find((obj) => obj.name === 'lastname_applicant')?.value;

    return `Let op: Het IBAN rekeningnummer dient op naam te staan van ${initials} ${lastname}.`;
  }
  return '';
};

export const IbanInput = ({ name, ...props }) => {
  const context = useContext(ComparatorContext);
  const form = useFormService();
  const field = useField(`*.${name}`, form);
  const [footnoteLabel, setFootnoteLabel] = useState('');
  const [showFootnote, setShowFootnote] = useState(false);
  const [ibanInput, setIbanInput] = useState(field?.value || '');
  const [isABNprovider, setIsABNprovider] = useState(false);
  const [fieldHasError, setFieldHasError] = useState(false);

  const validateIban = useCallback(async () => {
    try {
      await checkIban(normalizeIBAN(ibanInput));
    } catch (error) {
      field?.set({
        errors: [InvalidIbanError],
      });
    }
  }, [ibanInput, field]);

  useEffect(() => {
    const getStateFromLocalStorage = context.getStateFromLocalStorage;
    const comparatorName = context.name;
    const persistedState = getStateFromLocalStorage();

    if (comparatorName === 'car') {
      const overview = persistedState?.context?.initialSteps[3];
      const apply = persistedState?.context?.initialSteps[5];
      const selectedProductField = overview?.fields.find(
        (f) => f.name === 'selectedProduct'
      )?.value;
      const applyForm = apply?.fields.find(
        (f) => f.name === 'wizardSteps'
      )?.value;
      const selectedProduct = JSON.parse(String(selectedProductField) || '');
      const stepOne = applyForm ? applyForm[0].fields : [];

      setIsABNprovider(selectedProduct.provider.id === '67');
      setFootnoteLabel(getFootnoteLabel(context, selectedProduct, stepOne));
    }
  }, [context]);

  useEffect(() => {
    setShowFootnote(field?.errors === undefined && isABNprovider);
    setFieldHasError(field?.errors !== undefined);
  }, [field?.errors, isABNprovider]);

  useEffect(() => {
    if (
      field?.errors === undefined &&
      field?.value &&
      isIBAN(ibanInput.toString()) &&
      ['car', 'energy', 'energy-auction'].includes(context.name)
    ) {
      validateIban();
    }
  }, [ibanInput, field?.errors, field?.value, context.name, validateIban]);

  if (!field) {
    return null;
  }

  return (
    <Div
      css={
        isABNprovider
          ? {
              display: 'flex',
              flexDirection: 'column',
              '>div:first-of-type': { paddingBottom: fieldHasError ? 30 : 0 },
            }
          : {}
      }
    >
      <BasicInput
        _fieldName={field.name}
        {...props}
        {...field}
        onBlur={(e) => {
          if (typeof field.onBlur === 'function') {
            field.onBlur(e);
            setIbanInput(field?.value as string);
          }
        }}
      />
      {showFootnote && footnoteLabel !== '' && (
        <P
          variant="footnoteRegularToCaptionSmall"
          fontWeight={0}
          mt={2}
          pb={6}
          color="second.1"
        >
          {footnoteLabel}
        </P>
      )}
    </Div>
  );
};
